import { Metamodel } from '../base/model';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { ModelConstants } from '../model-constants';
import { IViolation } from '../base/annotation';
import { ActionConstants } from '../../constants/action-constants';
import { IColumn, ITable } from './interface';
import { SharedModel } from '../sharedmodel';
import { DatabaseEntity } from '../database/databaseEntity';
import { getGeneralNamePatterns, validateName } from '../../shared/utils/validation.util';
import { TreeNamedResAndNameUtils } from '../../shared/utils/tree-named-naming.util';
import { format, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { ValidationConstants } from '../../shared/validation-constants';

@Metamodel.model({
    name: ModelConstants.table,
    display: [shellStringResources.SynapseTables],
    reusable: true,
    referable: true,
    validator: Table.validate,
    constraints: {
        realName: {
            required: true,
            uniqueModelName: true
        }
    },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.datasetsWrite
        },
        deleteResource: {
            action: ActionConstants.datasetsDelete
        }
    }
})
export class Table extends DatabaseEntity {
    public readonly excludedInArmTemplate = true;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Parent
    })
    public parentInfo: SharedModel.IParentInfo;

    @Metamodel.attribute({
        display: shellStringResources.Name
    })
    public name: string;

    @Metamodel.attribute({
        display: shellStringResources.Name
    })
    get realName(): string {
        const nameDict = TreeNamedResAndNameUtils.extractNameAndParentName(this.name);
        const name = nameDict.name;
        return name;
    }

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Description
    })
    public description?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Content
    })
    public content?: ITable;

    constructor(name: string, description: string, parentInfo: SharedModel.IParentInfo, tableContent: ITable) {
        super(name, description, parentInfo);
        this.content = tableContent;
        this.resourceType = ModelConstants.table + 's';
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static async validate(owner: any): Promise<IViolation[] | undefined> {
        const named = (owner && owner.named) as Table;
        const table = named.content;
        if (table) {
            if (table.newValidation) {
                if (table.violations) {
                    table.violations.forEach((violation: IViolation) => {
                        violation.context = owner;
                    });
                }
                return table.violations;
            }

            const violations: IViolation[] = [];

            const tableNamePatterns = getGeneralNamePatterns(shellStringResources.SynapseTables).slice().concat({
                regex: ValidationConstants.alphanumericUnderscore,
                message: format(shellStringResources.Validator_NamePattern_All_AlphanumericUnderscore, shellStringResources.Table)
            });

            tableNamePatterns.forEach((p) => {
                if (!p.regex.test(table.name)) {
                    violations.push({
                        context: owner,
                        message: () => p.message
                    });
                }
            });

            if (table.deltaFormatInvalidMessages) {
                violations.push(...table.deltaFormatInvalidMessages.map((message) => ({
                    context: owner,
                    message: () => sanitizeAndFormatString('{0} - {1}', message, table.name)
                })));
            }

            const columns: IColumn[] = table.storageDescriptor.columns;
            const nameDict: string[] = [];
            for (const column of columns) {
                const patternViolations = validateName(column.name, shellStringResources.Column);
                if (!ValidationConstants.alphanumericUnderscore.test(column.name)) {
                    patternViolations.push(format(shellStringResources.Validator_NamePattern_All_AlphanumericUnderscore, shellStringResources.Column));
                }
                if (patternViolations) {
                    for (const patternViolation of patternViolations) {
                        violations.push({
                            context: owner,
                            message: () => patternViolation
                        });
                    }
                } else {
                    if (nameDict.includes(column.name)) {
                        violations.push({
                            context: owner,
                            message: () => sanitizeAndFormatString(shellStringResources.Validator_Column_AlreadyExist, named.realName)
                        });
                    } else {
                        nameDict.push(column.name);
                    }
                }

                if ((column.deltaFormatInvalidMessages ?? []).length > 0) {
                    violations.push(...column.deltaFormatInvalidMessages.map((message) => ({
                        context: owner,
                        message: () => sanitizeAndFormatString('{0} - {1}({2})', message, table.name, column.name)
                    })));
                }

                const columnType = column.originDataTypeName;
                if (columnType.typeName === undefined || columnType.typeName === '') {
                    violations.push({
                        context: owner,
                        message: () => sanitizeAndFormatString(shellStringResources.Validator_Column_TypeRequired, named.realName)
                    });
                } else if (columnType.typeName === 'decimal' && (columnType.precision === undefined || columnType.scale === undefined)) {
                    violations.push({
                        context: owner,
                        message: () => sanitizeAndFormatString(shellStringResources.Validator_Column_DecimalFormatError, named.realName)
                    });
                } else if (columnType.typeName === 'string' && columnType.length === undefined) {
                    violations.push({
                        context: owner,
                        message: () => sanitizeAndFormatString(shellStringResources.Validator_Column_StringLengthError, named.realName)
                    });
                } else if (columnType.typeName === 'string' && columnType.length > ValidationConstants.dataTypeLengthMaxlimit) {
                    violations.push({
                        context: owner,
                        message: () => format(shellStringResources.Validator_Column_MaxStringLengthError, ValidationConstants.dataTypeLengthMaxlimit.toString())
                    });
                }
            }

            const storageUrlSimpleRegex = /^abfss?:\/\/(.+)\@([^\/.\s]+)\.(.*(?=\/))\/(.+)$/i;
            if (table?.storageDescriptor?.source?.location && !table.storageDescriptor.source.location.match(storageUrlSimpleRegex)) {
                violations.push({
                    context: owner,
                    message: () => sanitizeAndFormatString(shellStringResources.Validator_Table_StorageLocationIncorrect, table.name)
                });
            }

            return violations;
        }
        return undefined;
    }
}
